import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Crown, Loader } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'error'
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentInfo(response.data);
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
        <Card className="glass p-12 max-w-lg w-full text-center">
          <Loader className="w-16 h-16 text-blood-400 mx-auto mb-6 animate-spin" />
          <h2 className="text-2xl font-bold text-white mb-4 tracking-wider">PROCESSING PAYMENT...</h2>
          <p className="text-stone-400">Please wait while we confirm your payment.</p>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
        <Card className="glass p-12 max-w-lg w-full text-center">
          <div className="text-6xl mb-6">❌</div>
          <h2 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">PAYMENT VERIFICATION FAILED</h2>
          <p className="text-stone-400 mb-8">
            We couldn't verify your payment. If you were charged, please contact support.
          </p>
          <Button
            onClick={() => navigate('/main-hub')}
            className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-semibold px-8 py-3 rounded-xl"
          >
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
      <Card className="glass p-12 max-w-2xl w-full text-center">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <CheckCircle className="w-24 h-24 text-blood-500" />
            <Crown className="w-12 h-12 text-yellow-400 absolute -top-2 -right-2 animate-pulse" />
          </div>
        </div>
        
        <h1 className="text-4xl font-bold text-blood-400 mb-4 tracking-wider">
          PAYMENT SUCCESSFUL! ⚔️
        </h1>
        
        <p className="text-2xl text-white mb-2 font-semibold">
          Welcome to Premium!
        </p>
        
        <p className="text-stone-400 mb-8">
          Your payment has been processed and premium features are now activated.
        </p>

        {/* Premium Features Unlocked */}
        <div className="glass p-6 rounded-xl mb-8 border border-blood-700">
          <h3 className="text-xl font-bold text-blood-400 mb-4">🔓 UNLOCKED FEATURES</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <div className="text-stone-300">
              <span className="text-blood-400 mr-2">✓</span>
              AI Workout Generation
            </div>
            <div className="text-stone-300">
              <span className="text-blood-400 mr-2">✓</span>
              USDA Food Database
            </div>
            <div className="text-stone-300">
              <span className="text-blood-400 mr-2">✓</span>
              Barcode Scanner
            </div>
            <div className="text-stone-300">
              <span className="text-blood-400 mr-2">✓</span>
              Advanced Analytics
            </div>
            <div className="text-stone-300">
              <span className="text-blood-400 mr-2">✓</span>
              Exercise Substitutions
            </div>
            <div className="text-stone-300">
              <span className="text-blood-400 mr-2">✓</span>
              Intermittent Fasting
            </div>
          </div>
        </div>

        {paymentInfo && (
          <div className="glass p-4 rounded-lg mb-8 text-stone-400 text-sm">
            <p>Payment Amount: ${(paymentInfo.amount || 0).toFixed(2)} {paymentInfo.currency?.toUpperCase()}</p>
            <p className="text-xs text-stone-500 mt-1">Session ID: {sessionId}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/main-hub')}
            className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-semibold px-8 py-3 rounded-xl tracking-wider"
          >
            START YOUR JOURNEY
          </Button>
          <Button
            onClick={() => navigate('/nutrition')}
            variant="outline"
            className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold px-8 py-3 rounded-xl"
          >
            EXPLORE NUTRITION
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
